import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import {
  OrganizationData,
  QueryParams,
  OfflineQueueItem,
  FetchOrganizationsResponse,
  OrganizationState,
  OrganizationDataResponse,
  deptFormData,
} from "./organizationTypes";
import apiService from "../../services/api.services";


const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const OFFLINE_STORAGE_KEY = "organization_offline_data";

// Helper to create cache key from query params
const createCacheKey = (params: QueryParams): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
};

const initialState: OrganizationState = {
  organizations: {},
  status: "idle",
  error: null,
  total: 0,
  currentPage: 1,
  organizationId: null,
  lastFetched: {},
  pendingUpdates: {},
  offlineQueue: [],
  activeRequests: {},
  retryCount: {},
  stats: {
    data: {
      tutors: {
        data: [],
        total: 0,
      },
      students: {
        data: [],
        total: 0,
      },
      courses: {
        data: [],
        total: 0,
      },
      departments: {
        data: [],
        total: 0,
      },
    },
    status: "idle",
    error: null,
  },
};

// Helper function to handle offline storage
const saveToOfflineStorage = (data: Record<number, OrganizationData>): void => {
  try {
    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to offline storage:", error);
  }
};

const loadFromOfflineStorage = (): Record<number, OrganizationData> | null => {
  try {
    const data = localStorage.getItem(OFFLINE_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to load from offline storage:", error);
    return null;
  }
};

export const createOrganization = createAsyncThunk<
  OrganizationData,
  { organizationData: FormData },
  { rejectValue: { message: string } }
>(
  "organizations/createdepartment",
  async ({ organizationData }, { rejectWithValue }) => {
    try {
      const response = await apiService.post<OrganizationData>(
        `/organizations/register`,
        organizationData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success("Organization created successfully!");
      return response;
    } catch (error) {
      console.error("Failed to create organization:", error);
      const axiosError = error as AxiosError<{
        message: string;
        error: string;
      }>;
      const errorMessage =
        axiosError.response?.data?.error || "Failed to create organization";
      toast.error(errorMessage);
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const createDepartment = createAsyncThunk<
  OrganizationData,
  { departmentData: deptFormData },
  { rejectValue: { message: string } }
>(
  "organizations/createOrganization",
  async ({ departmentData }, { rejectWithValue }) => {
    try {
      const response = await apiService.post<OrganizationData>(
        `/organizations/create-department`,
        departmentData,
      );

      toast.success("department created successfully!");
      return response;
    } catch (error) {
      console.error("Failed to create department:", error);
      const axiosError = error as AxiosError<{
        message: string;
        error: string;
      }>;
      const errorMessage =
        axiosError.response?.data?.error || "Failed to create department";
      toast.error(errorMessage);
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const fetchOrganizations = createAsyncThunk<
  {
    organizations: OrganizationData[];
    pagination: {
      total: number;
      currentPage: number;
      totalPages: number;
    };
    cacheKey: string;
  } | null,
  QueryParams,
  {
    state: { organizations: OrganizationState };
    rejectValue: { message: string };
  }
>(
  "organizations/fetchOrganizations",
  async (params, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const cacheKey = createCacheKey(params);
    const requestId = `fetch_${cacheKey}`;

    // Check if request is already in progress
    if (state.organizations.activeRequests[requestId]) {
      return null; // Skip duplicate request
    }

    // Check offline status
    if (!navigator.onLine) {
      const offlineData = loadFromOfflineStorage();
      if (offlineData) {
        return {
          organizations: Object.values(offlineData),
          pagination: {
            total: Object.keys(offlineData).length,
            currentPage: 1,
            totalPages: 1,
          },
          cacheKey,
        };
      }
      return rejectWithValue({ message: "You are offline" });
    }

    // Check cache
    const lastFetched = state.organizations.lastFetched[cacheKey] || 0;
    if (Date.now() - lastFetched < CACHE_DURATION) {
      return null;
    }

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const queryParams = new URLSearchParams({
          page: String(params.page || 1),
          limit: String(params.limit || 10),
          ...(params.type && { type: params.type }),
          ...(params.country && { country: params.country }),
        });

        dispatch(setActiveRequest(requestId));
        const response = await apiService.get<FetchOrganizationsResponse>(
          `/organizations/list?${queryParams}`,
        );

        const organizations = response.data?.organizations || [];
        const normalizedData = organizations.reduce(
          (acc, org) => ({
            ...acc,
            [String(org.id)]: org,
          }),
          {} as Record<number, OrganizationData>
        );
        saveToOfflineStorage(normalizedData);

        return {
          organizations,
          pagination: response.data?.pagination || {
            total: 0,
            currentPage: 1,
            totalPages: 1,
          },
          cacheKey,
        };
      } catch (error) {
        retryCount++;
        if (retryCount === MAX_RETRIES) {
          const axiosError = error as AxiosError<{ message: string }>;
          toast.error(
            axiosError.response?.data?.message ||
            "Failed to fetch organizations"
          );
          return rejectWithValue({
            message:
              axiosError.response?.data?.message ||
              "Failed to fetch organizations",
          });
        }
        // Wait before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * retryCount)
        );
      } finally {
        dispatch(removeActiveRequest(requestId));
      }
    }

    return rejectWithValue({ message: "Max retries exceeded" });
  }
);

export const fetchOrganizationTutors = createAsyncThunk<
  OrganizationDataResponse,
  number,
  { rejectValue: { message: string } }
>(
  "organizations/fetchOrganizationStats",
  async (organizationId, { rejectWithValue }) => {
    try {
      const response = await apiService.get<{ stats: OrganizationDataResponse }>(
        `/organizations/${organizationId}/tutors/`,
      );
      return response.stats;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message ||
        "Failed to fetch organization tutors"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message ||
          "Failed to fetch organization tutors",
      });
    }
  }
);

export const updateOrganization = createAsyncThunk<
  {
    id: number;
    updates: OrganizationData;
    optimisticId: string;
    isOffline?: boolean;
  },
  { id: number; updates: Partial<OrganizationData> },
  {
    rejectValue: { error: any; optimisticId: string; id: number };
  }
>(
  "organizations/updateOrganization",
  async ({ id, updates }, { dispatch, rejectWithValue }) => {
    const optimisticId = `update_${id}_${Date.now()}`;

    // If offline, queue the update
    if (!navigator.onLine) {
      dispatch(
        addToOfflineQueue({
          type: "UPDATE",
          payload: { id, updates },
          timestamp: Date.now(),
        })
      );
      return {
        id,
        updates: updates as OrganizationData,
        optimisticId,
        isOffline: true,
      };
    }

    try {
      const response = await apiService.put<OrganizationData>(
        `/organizations/${id}`,
        updates
      );
      toast.success("Organization updated successfully");
      return { id, updates: response, optimisticId };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to update organization"
      );
      return rejectWithValue({
        error: axiosError.response?.data,
        optimisticId,
        id,
      });
    }
  }
);

export const deleteOrganization = createAsyncThunk(
  "organizations/deleteOrganization",
  async (
    { id, updates }: { id: number; updates: Partial<OrganizationData> },
    { dispatch, rejectWithValue }
  ) => {
    const optimisticId = `update_${id}_${Date.now()}`;

    // If offline, queue the update
    if (!navigator.onLine) {
      dispatch(
        addToOfflineQueue({
          type: "DELETE",
          payload: { id, updates },
          timestamp: Date.now(),
        })
      );
      return { id, updates, optimisticId, isOffline: true };
    }

    try {
      const response = await apiService.put(
        `/organizations/${id}`,
        updates
      );
      toast.success("Organization deleted successfully");
      return { id, updates: response, optimisticId };
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to delete organization"
      );
      return rejectWithValue({
        error: error.response?.data,
        optimisticId,
        id,
      });
    }
  }
);

// Process offline queue when back online
export const processOfflineQueue = createAsyncThunk(
  "organizations/processOfflineQueue",
  async (_, { getState, dispatch }) => {
    const state = getState() as { organizations: OrganizationState };
    const queue = [...state.organizations.offlineQueue];

    for (const item of queue) {
      try {
        switch (item.type) {
          case "UPDATE":
            await dispatch(updateOrganization(item.payload)).unwrap();
            break;
          case "DELETE":
            await dispatch(deleteOrganization(item.payload)).unwrap();
            break;
        }
        dispatch(removeFromOfflineQueue(item));
      } catch (error) {
        console.error("Failed to process offline item:", error);
      }
    }
  }
);

const organizationSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setOrganizationId: (state, action: PayloadAction<number>) => {
      state.organizationId = action.payload;
    },
    invalidateCache: (state) => {
      state.lastFetched = {};
    },
    setActiveRequest: (state, action: PayloadAction<string>) => {
      state.activeRequests[action.payload] = true;
    },
    removeActiveRequest: (state, action: PayloadAction<string>) => {
      delete state.activeRequests[action.payload];
    },
    addToOfflineQueue: (state, action: PayloadAction<OfflineQueueItem>) => {
      state.offlineQueue.push(action.payload);
      saveToOfflineStorage(state.organizations);
    },
    removeFromOfflineQueue: (
      state,
      action: PayloadAction<OfflineQueueItem>
    ) => {
      state.offlineQueue = state.offlineQueue.filter(
        (item: OfflineQueueItem) => item.timestamp !== action.payload.timestamp
      );
    },
    resetOrganizations: (state) => {
      state.organizations = {};
      state.total = 0;
      state.currentPage = 1;
      state.status = "idle";
      state.error = null;
      state.lastFetched = {};
      state.pendingUpdates = {};
      state.offlineQueue = [];
      state.activeRequests = {};
      state.retryCount = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrganization.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.id !== undefined) {
          state.organizations[action.payload.id] = action.payload;
        }
        state.total += 1;
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || "Failed to create organization";
      })
      .addCase(createDepartment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.id !== undefined) {
          state.organizations[action.payload.id] = action.payload;
        }
        state.total += 1;
      })
      .addCase(createDepartment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || "Failed to create organization";
      })
      .addCase(fetchOrganizations.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        if (action.payload) {
          const { organizations, pagination, cacheKey } = action.payload;

          state.organizations = organizations.reduce(
            (acc, org) => ({
              ...acc,
              ...(org.id !== undefined ? { [org.id.toString()]: org } : {}),
            }),
            {} as Record<string, OrganizationData>
          );

          state.total = pagination.total;
          state.currentPage = pagination.currentPage;
          state.lastFetched[cacheKey] = Date.now();
          state.status = "succeeded";
        }
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || "Failed to fetch organizations";
      })
      .addCase(processOfflineQueue.fulfilled, (state) => {
        state.offlineQueue = [];
      })
      .addCase(fetchOrganizationTutors.pending, (state) => {
        state.stats.status = "loading";
        state.stats.error = null;
      })
      .addCase(fetchOrganizationTutors.fulfilled, (state, action) => {
        state.stats.status = "succeeded";
        state.stats.data = {
          tutors: action.payload.tutors,
          students: action.payload.students,
          courses: action.payload.courses,
          departments: action.payload.departments,
        };
      })
      .addCase(fetchOrganizationTutors.rejected, (state, action) => {
        state.stats.status = "failed";
        state.stats.error =
          action.payload?.message || "Failed to fetch organization tutors";
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        if (!action.payload.isOffline) {
          state.organizations[action.payload.id] = action.payload.updates;
        }
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        if (!action.payload.isOffline) {
          delete state.organizations[action.payload.id];
        }
      });
  },
});

// Export actions
export const {
  setCurrentPage,
  setOrganizationId,
  invalidateCache,
  setActiveRequest,
  removeActiveRequest,
  addToOfflineQueue,
  removeFromOfflineQueue,
  resetOrganizations,
} = organizationSlice.actions;

// Selectors with proper typing
export const selectOrganizations = createSelector(
  [
    (state: { organizations: OrganizationState }) =>
      state.organizations.organizations,
  ],
  (organizations): OrganizationData[] => Object.values(organizations)
);

export const selectOrganizationId = createSelector(
  [(state: { organizations: OrganizationState }) => state.organizations],
  (organizations: OrganizationState): number | null =>
    organizations.organizationId ?? null
);

export const selectOrganizationById = createSelector(
  [
    (state: { organizations: OrganizationState }) =>
      state.organizations.organizations,
    (_: any, id: number) => id,
  ],
  (organizations, id): OrganizationData | undefined => organizations[id]
);

export const selectOrganizationStats = createSelector(
  [(state: { organizations: OrganizationState }) => state.organizations.stats],
  (stats) => stats.data
);

export const selectOrganizationStatsTutors = createSelector(
  [
    (state: { organizations: OrganizationState }) =>
      state.organizations.stats.data,
  ],
  (data) => data.tutors
);

export const selectOrganizationStatsStudents = createSelector(
  [
    (state: { organizations: OrganizationState }) =>
      state.organizations.stats.data,
  ],
  (data) => data.students
);

export const selectOrganizationCourses = createSelector(
  [
    (state: { organizations: OrganizationState }) =>
      state.organizations.stats.data,
  ],
  (data) => data.courses
);

export const selectOrganizationStatsDepartments = createSelector(
  [
    (state: { organizations: OrganizationState }) =>
      state.organizations.stats.data,
  ],
  (data) => data.departments
);
export const selectOrganizationTutorsError = createSelector(
  [(state: { organizations: OrganizationState }) => state.organizations.stats],
  (stats) => stats.error
);

// Setup online/offline listeners
export const setupNetworkListeners = (store: any) => {
  window.addEventListener("online", () => {
    store.dispatch(processOfflineQueue());
  });

  window.addEventListener("offline", () => {
    toast.info("You are offline. Changes will be saved locally.");
  });
};

export default organizationSlice.reducer;
